/**
 * File Upload Configuration
 * Central configuration for file upload limits and settings
 */

export const FILE_UPLOAD_CONFIG = {
  // Maximum file size in bytes (10MB)
  MAX_FILE_SIZE: 10 * 1024 * 1024,

  // Maximum number of files that can be uploaded
  MAX_FILES: 5,

  // Image compression quality (0-1)
  IMAGE_QUALITY: 0.8,

  // Chunk size for large file processing (1MB)
  CHUNK_SIZE: 1024 * 1024,

  // Supported MIME types
  SUPPORTED_MIME_TYPES: {
    // Images
    'image/jpeg': ['jpg', 'jpeg'],
    'image/png': ['png'],
    'image/gif': ['gif'],
    'image/webp': ['webp'],
    'image/bmp': ['bmp'],

    // Documents
    'application/pdf': ['pdf'],
    'application/msword': ['doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      'docx',
    ],
    'application/vnd.ms-excel': ['xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      'xlsx',
    ],
    'text/plain': ['txt'],
    'text/csv': ['csv'],
  },

  // Get all supported extensions
  get SUPPORTED_EXTENSIONS() {
    return Object.values(this.SUPPORTED_MIME_TYPES).flat();
  },

  // Get all supported MIME type keys
  get SUPPORTED_MIME_TYPE_LIST() {
    return Object.keys(this.SUPPORTED_MIME_TYPES);
  },

  // Error messages
  ERROR_MESSAGES: {
    FILE_TOO_LARGE: 'File size exceeds maximum limit of 10MB',
    UNSUPPORTED_FORMAT: 'Unsupported file format',
    MAX_FILES_REACHED: 'Maximum number of files reached',
    FILE_NOT_FOUND: 'File not found',
    CONVERSION_FAILED: 'Failed to process file',
    NETWORK_ERROR: 'Network error. Please check your connection',
  },
};

/**
 * Check if a file extension is supported
 * @param {string} extension - File extension without dot
 * @returns {boolean}
 */
export function isSupportedExtension(extension) {
  return FILE_UPLOAD_CONFIG.SUPPORTED_EXTENSIONS.includes(
    extension.toLowerCase()
  );
}

/**
 * Check if a MIME type is supported
 * @param {string} mimeType - MIME type
 * @returns {boolean}
 */
export function isSupportedMimeType(mimeType) {
  return FILE_UPLOAD_CONFIG.SUPPORTED_MIME_TYPE_LIST.includes(mimeType);
}

/**
 * Get MIME type from file extension
 * @param {string} extension - File extension without dot
 * @returns {string|null}
 */
export function getMimeTypeFromExtension(extension) {
  const ext = extension.toLowerCase();
  for (const [mimeType, extensions] of Object.entries(
    FILE_UPLOAD_CONFIG.SUPPORTED_MIME_TYPES
  )) {
    if (extensions.includes(ext)) {
      return mimeType;
    }
  }
  return null;
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default FILE_UPLOAD_CONFIG;
